import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'
import { AxiosResponse } from 'axios'
import { toast } from 'react-toastify'
import { IPaginatedResponse } from 'types/api/general.interface'
import {
  IGetAllResourceBookingsSingleResponse,
  IGetSingleResourceBookingResponse,
  IResourceBookingPostRequest,
} from 'types/api/operationsBookingApi.interface'
import { IBookingSliceInitalState, IResourceBookingState } from 'types/operationBooking.interface'
import { baseUrl } from 'utils/axios'
import { handleErrors } from 'utils/helpers'

const initialState: IBookingSliceInitalState = {
  isLoading: false,
  data: [],
  totalCount: 0,
  totalPages: 0,
  singleBooking: null,
}

export const getResourceBookings = createAsyncThunk(
  'resourceBookings/getResourceBookings',
  async (params: any, thunkAPI) => {
    try {
      const resp: AxiosResponse<IPaginatedResponse<IGetAllResourceBookingsSingleResponse>> =
        await baseUrl.get('aim-operations/resource-booking', {
          params,
        })
      return resp.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const getSingleResourceBooking = createAsyncThunk(
  'resourceBookings/getSingleResourceBooking',
  async (id: string, thunkAPI) => {
    try {
      const resp: AxiosResponse<IGetSingleResourceBookingResponse> = await baseUrl.get(
        'aim-operations/resource-booking/' + id,
      )
      return resp.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const addResourceBooking = createAsyncThunk(
  'resourceBookings/addResourceBooking',
  async (state: IResourceBookingState, thunkAPI) => {
    const preemptLowerPriority = state.preemptLowerPriority === true

    const req: IResourceBookingPostRequest = {
      resourceType: Number(state.resourceType?.value),
      resourceId: Number(state.resource?.value),
      holderType: Number(state.holderType?.value),
      holderId: Number(state.holder?.value),
      priority: Number(state.priority?.value),
      startAt: state.startAt?.toISOString(),
      endAt: state.endAt?.toISOString(),
      preemptLowerPriority,
    }

    try {
      const resp: AxiosResponse = await baseUrl.post('aim-operations/resource-booking', req)
      return resp.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const releaseResourceBooking = createAsyncThunk(
  'resourceBookings/releaseResourceBooking',
  async ({ id }: { id: number }, thunkAPI) => {
    try {
      const resp: AxiosResponse = await baseUrl.post(
        `aim-operations/resource-booking/${id}/release`,
      )
      return resp.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const sweepExpiredResourceBookings = createAsyncThunk(
  'resourceBookings/sweepExpiredResourceBookings',
  async (_, thunkAPI) => {
    try {
      const resp: AxiosResponse = await baseUrl.post(
        'aim-operations/resource-booking/sweep-expired',
      )
      return resp.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

const resourceBookingsSlice = createSlice({
  name: 'resourceBookings',
  initialState,
  reducers: {
    clearResourceBookings: (state) => {
      state.data = []
      state.totalCount = 0
      state.totalPages = 0
    },
    clearSingleResourceBooking: (state) => {
      state.singleBooking = null
    },
  },
  extraReducers: {
    [getResourceBookings.pending.type]: (state) => {
      state.isLoading = true
    },
    [getResourceBookings.fulfilled.type]: (
      state,
      { payload }: { payload: IPaginatedResponse<IGetAllResourceBookingsSingleResponse> },
    ) => {
      state.isLoading = false
      state.data = payload.data
      state.totalCount = payload.totalCount
      state.totalPages = payload.totalPages
    },
    [getResourceBookings.rejected.type]: (state, { payload }) => {
      state.isLoading = false
      handleErrors(payload)
    },
    [getSingleResourceBooking.pending.type]: (state) => {
      state.isLoading = true
    },
    [getSingleResourceBooking.fulfilled.type]: (
      state,
      { payload }: { payload: IGetSingleResourceBookingResponse },
    ) => {
      state.isLoading = false
      state.singleBooking = payload
    },
    [getSingleResourceBooking.rejected.type]: (state, { payload }) => {
      state.isLoading = false
      handleErrors(payload)
    },
    [addResourceBooking.pending.type]: (state) => {
      state.isLoading = true
    },
    [addResourceBooking.fulfilled.type]: (state) => {
      state.isLoading = false
      toast.success('Resource booking created successfully')
    },
    [addResourceBooking.rejected.type]: (state, { payload }) => {
      state.isLoading = false
      handleErrors(payload)
    },
    [releaseResourceBooking.pending.type]: (state) => {
      state.isLoading = true
    },
    [releaseResourceBooking.fulfilled.type]: (state) => {
      state.isLoading = false
      toast.success('Resource booking released successfully')
    },
    [releaseResourceBooking.rejected.type]: (state, { payload }) => {
      state.isLoading = false
      handleErrors(payload)
    },
    [sweepExpiredResourceBookings.pending.type]: (state) => {
      state.isLoading = true
    },
    [sweepExpiredResourceBookings.fulfilled.type]: (state) => {
      state.isLoading = false
      toast.success('Expired resource bookings swept successfully')
    },
    [sweepExpiredResourceBookings.rejected.type]: (state, { payload }) => {
      state.isLoading = false
      handleErrors(payload)
    },
  },
})

export const { clearResourceBookings, clearSingleResourceBooking } = resourceBookingsSlice.actions

export default resourceBookingsSlice.reducer
